import { Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client';
import { Apollo, gql } from 'apollo-angular';
import { HttpService } from './http.service';
import { Observable } from 'rxjs';
import { SiteHoleTolerance } from '../bo/site-hole-tolerance';



export enum HoleStatus {
  UNSET = "Unset",
  PLANNED = "Planned",
  DRILLED = "Drilled",
  TO_REDRILL = "ToRedrill",
  REDRILLED = "Redrilled",
  TOADJUST = "ToAdjust",
  MISSING = "Missing",
  COLLAPSED = "Collapsed",
  LOADING = "Loading",
  LOADED = "Loaded",
  BLASTED = "Blasted",
}

// Enum qui correspond exactement à HoleStatusField en C#
export enum HoleStatusField {
  Blasted = 1,
  Drilled = 1 << 1,      // 2
  Collapsed = 1 << 2,    // 4
  Loaded = 1 << 3,       // 8
  Loading = 1 << 4,      // 16
  Planned = 1 << 5,      // 32
  Missing = 1 << 6,      // 64
  Redrilled = 1 << 7,    // 128
  ToRedrill = 1 << 8,    // 256
  ToAdjust = 1 << 9,     // 512
}

export enum HoleTypeQuality {
  Head = 'HEAD',
  Length = 'LENGTH',
  Toe = 'TOE'
}

export enum LoadingHoleTypeQuality {
  Bulk = 'BULK',
  StreamingFinal = 'STREAMING_FINAL',
  LinearQuantity = 'LINEAR_QUANTITY'
}

export class HoleStatusHistoryUtil {
  private static readonly STATUS_TO_FIELD = new Map<HoleStatus, HoleStatusField>([
    [HoleStatus.BLASTED, HoleStatusField.Blasted],
    [HoleStatus.DRILLED, HoleStatusField.Drilled],
    [HoleStatus.COLLAPSED, HoleStatusField.Collapsed],
    [HoleStatus.LOADED, HoleStatusField.Loaded],
    [HoleStatus.LOADING, HoleStatusField.Loading],
    [HoleStatus.PLANNED, HoleStatusField.Planned],
    [HoleStatus.MISSING, HoleStatusField.Missing],
    [HoleStatus.REDRILLED, HoleStatusField.Redrilled],
    [HoleStatus.TO_REDRILL, HoleStatusField.ToRedrill],
    [HoleStatus.TOADJUST, HoleStatusField.ToAdjust],
    // UNSET n'existe pas dans l'enum C#, donc on l'ignore
  ]);

  /**
   * Vérifie si un trou a eu un statut particulier dans son historique
   * @param holeStatusHistory - Le masque binaire de l'historique des statuts (HoleStatusField)
   * @param status - Le statut à vérifier
   * @returns true si le trou a eu ce statut
   */
  static hasHadStatus(holeStatusHistory: number, status: HoleStatus): boolean {
    const fieldValue = this.STATUS_TO_FIELD.get(status);
    if (fieldValue === undefined) {
      // Si le statut n'est pas dans la map (ex: UNSET), retourner false
      return false;
    }
    return (holeStatusHistory & fieldValue) !== 0;
  }

  /**
   * Vérifie si un trou a eu au moins un des statuts spécifiés
   * @param holeStatusHistory - Le masque binaire de l'historique des statuts (HoleStatusField)
   * @param statuses - Les statuts à vérifier
   * @returns true si le trou a eu au moins un de ces statuts
   */
  static hasHadAnyStatus(holeStatusHistory: number, statuses: Set<HoleStatus>): boolean {
    return Array.from(statuses).some(status => this.hasHadStatus(holeStatusHistory, status));
  }

  /**
   * Debug : Retourne la liste des statuts qui ont été traversés par ce trou
   * @param holeStatusHistory - Le masque binaire de l'historique des statuts
   * @returns Array des statuts qui ont été traversés
   */
  static getStatusHistory(holeStatusHistory: number): HoleStatus[] {
    const history: HoleStatus[] = [];
    for (const [status, fieldValue] of this.STATUS_TO_FIELD) {
      if ((holeStatusHistory & fieldValue) !== 0) {
        history.push(status);
      }
    }
    return history;
  }

  /**
   * Debug : Analyse détaillée d'un masque binaire
   * @param holeStatusHistory - Le masque binaire de l'historique des statuts
   * @returns Objet avec l'analyse détaillée
   */
  static analyzeStatusHistory(holeStatusHistory: number): {
    decimal: number;
    binary: string;
    statuses: HoleStatus[];
    details: { status: HoleStatus; field: HoleStatusField; present: boolean }[];
  } {
    const binary = holeStatusHistory.toString(2).padStart(10, '0');
    const statuses = this.getStatusHistory(holeStatusHistory);
    
    const details = Array.from(this.STATUS_TO_FIELD.entries()).map(([status, fieldValue]) => ({
      status,
      field: fieldValue,
      present: (holeStatusHistory & fieldValue) !== 0
    })).sort((a, b) => a.field - b.field);

    return {
      decimal: holeStatusHistory,
      binary,
      statuses,
      details
    };
  }
}

(window as any).HoleStatusHistoryUtil = HoleStatusHistoryUtil;


export interface PositionLGZ {
  latitude: number;
  longitude: number;
  altitude: number;
}
export interface PositionXYZ {
  x: number;
  y: number;
  z: number;
}

export interface HoleIdentity {
  HoleStatusHistory: number; // Masque binaire correspondant à HoleStatusField en C#
  // Autres propriétés de HoleIdentity si nécessaires :
  // ExternalGuid?: string;
  // SiteId?: string; 
  // Name?: string;
  // CurrentHoleState?: string;
  // IsDeleted?: boolean;
}

export interface DeviationSegment {
  x: number; 
  y: number;
  z: number;
}

export interface HoleGeometryDto {
  length: number;
  diameter: number;
  angle: number;
  azimuth: number;
  Deviation?: {
    segments: DeviationSegment[];
  };
  comments: string[];
  Design?: HoleGeometryQaQcDto;
  Measure?: HoleGeometryQaQcDto;
  surveyed: boolean;
  drilllength: number | null;
}

export interface HoleGeometryQaQcDto {
  length: number;
  diameter: number;
  angle: number;
  azimuth: number;
  surveyed: boolean;
}

export interface HoleLoading {
  actualCharge: {
    totalBulk: number;
    totalCartridges: number;
    chargeElements?: Charges[];
  }
  finalStemmingHeight: number;
  plannedCharge: HolesCharges;
  loadingStartTime?: string;
}

export interface Hole {
  publicGuid: string;
  name: string;
  hasCharge: boolean;
  drillingDate?: string;
  Geometry: HoleGeometryDto;
  comments: string[];
  Status: string;
  PositionLGZ: PositionLGZ;
  PositionPlannedXYZ: PositionXYZ;
  PositionMeasuredXYZ: PositionXYZ;
  Loading: HoleLoading;
  statusHistory?: number; 
  hasWater: boolean;
  hasTube: boolean;
  calculatedValues?: {
    headPositionMagnitude: number | null;
    toePositionMagnitude: number | null;
    toeLevelData: {
      planned: number | null;
      measured: number | null;
      difference: number | null;
    };
  };
}

export interface HoleDTO {
  publicGuid: string;
  name: string;
  plannedGeometry: HoleGeometryDto;
  measuredGeometry: HoleGeometryDto;
  status: string;
  positionLGZ: PositionLGZ;
  PositionPlannedXYZ: PositionXYZ;
  PositionMeasuredXYZ: PositionXYZ;
  identity?: HoleIdentity; 
  statusHistory?: number;
  hasWater: boolean;
  hasTube: boolean;
  hasDeviation: boolean;
  drillLogs?: DrillLog[];
}

export interface DrillLog {
  id: number;
  holeDrillLogId: number;
  discontinuityId: number;
  name: string;
  fromDepth: number;
  toDepth: number;
  drillingSessionId: number;
}

export interface HoleDetails {
  name: string;
  Loading: HoleLoading;
}

export interface Blast {
  tirId: number;
  nom: string;
  isSimpleLoading: boolean;
  Hole_Count: number;
  Holes: Hole[];
  BlastLoading: HoleLoad[];
  BlastDesign: Hole[];
}
export interface HolesCharges {
  chargeElements: Charges[];

}
export interface Charges {
  depth: number,
  indexFromBottom: number,
  isStemming: boolean,
  productId: number,
  quantity: number
}
export interface BlastHolesDTO {
  blastId: number;
  blastName: string;
  holes: HoleDTO[];
}
// hole-condition.interface.ts
export interface HoleCondition {
  shortName: string;
  id: number;
}

export interface Team {
  equipeId: number;
  nom: string;
}

export interface Operator {
  operatorId: number;
  nom: string;
  roles: string[];
}

export interface Equipement {
  equipementId: number;
  name: string;
}

export interface Detonator {
  id: number;
  commercialName: string;
  product: product;
  productId: number;
}

export interface Cartridge {
  id: number;
  fullName: string;
  product: product;
}

export interface product {
  id: number;
  productType: string;
}

export interface DeliveryParams {
  holeConditions: HoleCondition[];
  teams: Team[];
  operators: Operator[];
  equipements: Equipement[];
  detonators: Detonator[];
  cartridges: Cartridge[];
}

export interface HoleLegendProperties {
  holeStatus: HoleStatus;
  numberOfHoles: number;
  percentage?: number; 
  totalHoles?: number; 
}

export interface HoleFeatureLegendProperties {
  featureType: 'water' | 'tube' | 'deviation' | 'both' | 'none';
  numberOfHoles: number;
  percentage?: number;
  totalHoles?: number;
}

export interface HoleLoad{
  name: string;
  holeId: string;
  status: string;
  statusHistory: number;
  loadingDate: string;
  plannedTotalBulk: number;
  measuredTotalBulk: number;
  plannedSteaming: number;
  measuredSteaming: number;
  plannedLinearQt: number;
  measuredLinearQt: number;
  plannedCartridges: number;
  measuredCartridges: number;
  holePosition: PositionXYZ;
  comments: string[];
  hasWater: boolean;
  deliveryDate?: Date;
  operatorNames: string[];
  Geometry: HoleGeometryDto;
  positionLGZ: PositionLGZ;
}

const GET_BLASTS_HOLES = gql`
query  GetBlastsByIds($ids:[Int]){
  BlastsByIds(ids:$ids){
    tirId,
    nom,
    Holes{
      name,
      publicGuid,
      Status,
      statusHistory,
      Geometry{
        length,
        diameter
      },
      PositionLGZ{
        latitude,
        longitude
      }
      
    }
  }
}
`;

const GET_BLAST_HOLES_LOADING = gql`
query  GetBlasts($id:Int!){
  Blast(BlastId:$id){
    tirId,
    nom,
    isSimpleLoading,
    BlastLoading{
      name,
      holeId,
      status,
      loadingDate,
      statusHistory,
      plannedTotalBulk,
      measuredTotalBulk,
      plannedSteaming,
      measuredSteaming,
      plannedLinearQt,
      measuredLinearQt,
      plannedCartridges,
      measuredCartridges,
      holePosition{
        x,
        y,
        z
      },
      positionLGZ{
        latitude,
        longitude
      },
      comments,
      hasWater,
      deliveryDate,
      operatorNames
    }
  }
}
`;

const GET_BLAST_DESIGN = gql`
query  GetBlasts($id:Int!){
  Blast(BlastId:$id){
    tirId,
    nom,
    isSimpleLoading,
    BlastDesign{
      name,
      publicGuid,
      Status,
      drillingDate,
      statusHistory,
      hasWater,
      hasTube,
      comments,
      Geometry{
        length,
        diameter,
        azimuth,
        Design{
          angle,
          azimuth,
          diameter,
          length
        },
        Measure{
          angle,
          azimuth,
          diameter,
          length
        },
        drilllength,
        surveyed
      },
      PositionPlannedXYZ{
        x,
        y,
        z
      },
      PositionMeasuredXYZ{
        x,
        y,
        z
      },
      PositionLGZ{
        latitude,
        longitude
      }
      
    }
  }
}
`;

const GET_BLAST_HOLES = gql`
query  GetBlasts($id:Int!){
  Blast(BlastId:$id){
    tirId,
    nom,
    isSimpleLoading,
    Holes{
      name,
      publicGuid,
      Status,
      drillingDate,
      statusHistory,
      hasWater,
      hasTube,
      comments,
      Geometry{
        length,
        diameter,
        azimuth,
        Design{
          angle,
          azimuth,
          diameter,
          length
        },
        Measure{
          angle,
          azimuth,
          diameter,
          length
        },
        drilllength,
        surveyed
      },
      PositionPlannedXYZ{
        x,
        y,
        z
      },
      PositionMeasuredXYZ{
        x,
        y,
        z
      },
      PositionLGZ{
        latitude,
        longitude
      }
      
    }
  }
}
`;

const GET_BLAST_HOLESXYZ = gql`
query  GetBlasts($id:Int!){
  Blast(BlastId:$id){
    nom,
    Holes{
      name,
      publicGuid,
      Status,
      statusHistory,
      Geometry{
        length,
        angle,
        azimuth
        Deviation {
          segments {
            x
            y
            z
          }
        }
      },
      PositionPlannedXYZ{
        x,
        y,
        z
      },
      PositionMeasuredXYZ{
        x,
        y,
        z
      },
      drillLogs {
        id
        holeDrillLogId
        discontinuityId
        name
        fromDepth
        toDepth
        drillingSessionId
      },
      Loading{
            actualCharge{
                finalStemmingHeight,
                chargeElements{
                    depth,
                    indexFromBottom,
                    isStemming,
                    productId
                    quantity
                }
            }
            plannedCharge{
                finalStemmingHeight,
                chargeElements{
                    depth,
                    indexFromBottom,
                    isStemming,
                    productId
                    quantity
                }
            }
        }
    }
  }
}
`;


const GET_HOLES_NAMES_WITH_CHARGE = gql`
query  GetHolesNamesWithCharge($id:Int!){
  Blast(BlastId:$id){
    Holes{
      name,
      hasCharge
    }
  }
}
`;
const GET_HOLE_DETAIL = gql`
query HoleQuery($holeId: String!){
  Hole(HoleId:$holeId){
      name
      Loading{
          actualCharge{
              totalBulk
              totalCartridges
          }
      }
  }
}
`;

const GET_DELIVERY_PARAMS_QUERY = gql`
    query GetDeliveryParamsBySiteAndRegion($siteId: Int!) {
      DeliveryParamBysite(SiteId: $siteId) {
        holeConditions {
          shortName
          id
        }
        teams {
          equipeId
          nom
        }
        operators {
          operatorId
          nom
          roles
        }
        equipements{
          equipementId
          name
        }

      detonators{
        id,
        commercialName,
        product{id, productType}
        
      }
      cartridges{
        id,
        fullName,
        product{id, productType}
      }
      }
    }
  `;

@Injectable({
  providedIn: 'root'
})
export class HoleService {

  constructor(private readonly apollo: Apollo, private readonly http: HttpService) { }

  public GetBlastHoles(blastId: number,fetchPolicy: 'cache-first' | 'network-only' = 'cache-first'): Promise<Blast> {
    return new Promise((resolve, reject) => {
      this.apollo.query({
        query: GET_BLAST_HOLES,
        variables: {
          id: blastId
        },
        fetchPolicy
      }).subscribe((result: ApolloQueryResult<{ Blast }>) => {
        resolve(result.data.Blast);
      });

    });
  }

  public GetBlastDesign(blastId: number,fetchPolicy: 'cache-first' | 'network-only' = 'cache-first'): Promise<Blast> {
    
    return new Promise((resolve, reject) => {
      this.apollo.query({
        query: GET_BLAST_DESIGN,
        variables: {
          id: blastId
        },
        fetchPolicy
      }).subscribe((result: ApolloQueryResult<{ Blast }>) => {
        resolve(result.data.Blast);
      });

    });
  }

  public GetBlastLoading(blastId: number, fetchPolicy: 'cache-first' | 'network-only' = 'cache-first'): Promise<Blast> {
    return new Promise((resolve, reject) => {

      this.apollo.query({
        query: GET_BLAST_HOLES_LOADING,
        variables: {
          id: blastId
        },
        fetchPolicy
      }).subscribe((result: ApolloQueryResult<{ Blast }>) => {
        resolve(result.data.Blast);
        });

    });
  }

  public GetBlastHolePositions(blastId: number): Promise<Blast> {
    return new Promise((resolve, reject) => {

      this.apollo.query({
        query: GET_BLAST_HOLESXYZ,
        variables: {
          id: blastId
        }
      }).subscribe((result: ApolloQueryResult<{ Blast }>) => {
        resolve(result.data.Blast);
      });

    });
  }

  // public GetBlastsHoles(blastIds: number[]):Promise<Blast[]>{
  //   return new Promise( (resolve, reject)=> {
  //     this.apollo.query({
  //       query:GET_BLASTS_HOLES,
  //       variables:{
  //         ids: blastIds
  //       }
  //     }).subscribe((result: ApolloQueryResult<{BlastsByIds}>)=>{
  //       resolve(result.data.BlastsByIds);
  //     });

  //   });
  // }
  public GetBlastsHoles(blastIds: number[]): Promise<BlastHolesDTO[]> {
    if (blastIds.length == 0) return Promise.resolve([]);
    return new Promise((resolve, reject) => {
      this.http.post('blasts/blastHoles', blastIds)
        .subscribe((result: BlastHolesDTO[]) => {

          resolve(result);
        });

    });
  }

  public GetHolesNamesWithCharge(blastId: number): Promise<string[]> {
    return new Promise((resolve, reject) => {
      this.apollo.query({
        query: GET_HOLES_NAMES_WITH_CHARGE,
        variables: {
          id: blastId
        }
      }).subscribe((result: ApolloQueryResult<{ Blast }>) => {
        const holes: Hole[] = result.data.Blast.Holes;
        let names: string[] = holes.filter(h => h.hasCharge).map(x => x.name);
        resolve(names);
      });

    });
  }

  public GetHoleDetail(holeId: string): Promise<HoleDetails> {
    return new Promise((resolve, reject) => {
      this.apollo.query({
        query: GET_HOLE_DETAIL,
        variables: {
          holeId: holeId
        }
      }).subscribe((result: ApolloQueryResult<{ Hole }>) => {
        console.log(result.data);
        resolve(result.data.Hole);
      });

    });
  }

  getDeliveryParams(siteId: number): Promise<DeliveryParams> {
    return new Promise((resolve, reject) => {
      this.apollo.query({
        query: GET_DELIVERY_PARAMS_QUERY,
        variables: {
          siteId: siteId
        }
      }).subscribe(
        (result: ApolloQueryResult<{ DeliveryParamBysite: DeliveryParams }>) => {
          console.log(result.data);
          
          resolve(result.data.DeliveryParamBysite);
        },
        (error) => {
          console.error('Erreur de récupération des données', error);
          reject(error);
        }
      );
    });
  }


  getSiteHoleTolerance(siteId: number): Observable<SiteHoleTolerance> {
    return this.http.get<SiteHoleTolerance>(`Sites/${siteId}/hole-tolerance`);
  }

  putSiteHoleTolerance(siteId: number, dto: SiteHoleTolerance): Observable<SiteHoleTolerance> {
    return this.http.put<SiteHoleTolerance, SiteHoleTolerance>(`Sites/${siteId}/hole-tolerance`, dto);
  }

  getBlastLoadingQuality(): Observable<any> {
    return this.http.get<any>('Config/hole-loadquality');
  }

}